/**
 * What a card prints in front of a price, per ISO 4217 code — a STATIC table,
 * on purpose (#1489).
 *
 * The obvious source is `Intl.NumberFormat(..., { currencyDisplay:
 * 'narrowSymbol' })`, and the map pins and the calendar use exactly that. They
 * can: they are built in the browser only. A card is rendered on the server and
 * then hydrated, and Intl answers from the RUNTIME's copy of the Unicode
 * currency data — so the server and the visitor's browser each look the symbol
 * up in their own, and nothing makes the two copies agree. Measured while
 * reviewing this change, Node 22 (ICU 78) against Chrome 153, both en-US: 117
 * of 119 codes matched, and the Caribbean guilder did not — "Cg. 1,234" on the
 * server, "XCG 1,234" in the browser. Safari reads the operating system's data
 * and can trail by years. One differing character on an SSR'd card is a
 * hydration mismatch, and React throws the whole widget tree away on every load
 * (packages/homerunner-embeddables/AGENTS.md, SSR data contract).
 *
 * So the symbol is data too, and it ships with the bundle: identical bytes on
 * the server and in every browser, whatever either one knows about currencies.
 *
 * The rows are the en-US narrow-symbol prefixes on which Node and Chrome
 * agreed when this was written — generated from their output, not typed — so
 * for a listed code a current runtime prints the same on the card as on the pin
 * beside it. A code without a row prints as its ISO code and a no-break space.
 * That covers three cases, only the last of which differs from the pin:
 *   - the many whose narrow symbol IS their code (CHF, AED, PEN …), and any
 *     code Intl has never heard of, for which that is what Intl prints too;
 *   - XCG and XXX, left out BECAUSE the runtimes disagree about them;
 *   - KGS, agreed on but drawn with U+20C0, which almost no font carries yet —
 *     "KGS 1,234" beats an empty box in front of a price.
 *
 * Every character a reader cannot see (the no-break spaces, the narrow one
 * inside the West African franc) is written as an escape. Keep it that way: a
 * tidy-up that turns one into a plain space breaks parity with the pin and
 * nothing on screen shows it. Adding a currency is one word in one row.
 */
export const CURRENCY_PREFIX_ROWS: ReadonlyArray<readonly [prefix: string, codes: string]> = [
  ['$', 'USD CAD AUD NZD MXN SGD HKD TWD FJD ARS CLP COP UYU BZD DOP JMD BSD BBD TTD XCD KYD BMD BND CUC CUP GYD LRD NAD SBD SRD'],
  ['£', 'GBP FKP GIP SHP SSP SYP'],
  ['kr\u00a0', 'SEK NOK DKK ISK'],
  ['Rs\u00a0', 'MUR LKR NPR PKR'],
  ['¥', 'CNY JPY'],
  ['₩', 'KRW KPW'],
  ['֏', 'AMD'],
  ['؋', 'AFN'],
  ['৳', 'BDT'],
  ['฿', 'THB'],
  ['៛', 'KHR'],
  ['₡', 'CRC'],
  ['₦', 'NGN'],
  ['₪', 'ILS'],
  ['₫', 'VND'],
  ['€', 'EUR'],
  ['₭', 'LAK'],
  ['₮', 'MNT'],
  ['₱', 'PHP'],
  ['₲', 'PYG'],
  ['₴', 'UAH'],
  ['₸', 'KZT'],
  ['₹', 'INR'],
  ['₺', 'TRY'],
  ['₼', 'AZN'],
  ['₽', 'RUB'],
  ['₾', 'GEL'],
  ['Ar\u00a0', 'MGA'],
  ['Bs\u00a0', 'BOB'],
  ['C$', 'NIO'],
  ['CF\u00a0', 'KMF'],
  ['CFPF\u00a0', 'XPF'],
  ['Db\u00a0', 'STN'],
  ['E£', 'EGP'],
  ['F\u202fCFA\u00a0', 'XOF'],
  ['FCFA\u00a0', 'XAF'],
  ['FG\u00a0', 'GNF'],
  ['Ft\u00a0', 'HUF'],
  ['GH₵', 'GHS'],
  ['K\u00a0', 'MMK'],
  ['Kč\u00a0', 'CZK'],
  ['KM\u00a0', 'BAM'],
  ['kn\u00a0', 'HRK'],
  ['Kz\u00a0', 'AOA'],
  ['L\u00a0', 'HNL'],
  ['L£', 'LBP'],
  ['lei\u00a0', 'RON'],
  ['P\u00a0', 'BWP'],
  ['Q\u00a0', 'GTQ'],
  ['R\u00a0', 'ZAR'],
  ['R$', 'BRL'],
  ['RF\u00a0', 'RWF'],
  ['RM\u00a0', 'MYR'],
  ['Rp\u00a0', 'IDR'],
  ['T$', 'TOP'],
  ['ZK\u00a0', 'ZMW'],
  ['zł\u00a0', 'PLN'],
]

const PREFIX_BY_CODE = new Map<string, string>()
for (const [prefix, codes] of CURRENCY_PREFIX_ROWS) {
  for (const code of codes.split(' ')) PREFIX_BY_CODE.set(code, prefix)
}

/** `code` must already be three upper-case ASCII letters. */
export function currencyPrefix(code: string): string {
  return PREFIX_BY_CODE.get(code) ?? `${code}\u00a0`
}

/** Every code with an entry above — for the tests, not for callers. */
export const LISTED_CURRENCY_CODES: readonly string[] = [...PREFIX_BY_CODE.keys()]
